import { ChevronRight } from '@arvesta-websites/icons';
import type React from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { withErrorBoundary } from '../ErrorBoundary';
import { Heading } from '../Heading';

export interface FaqAccordionItem {
  /** Stable identifier — used as the React key and the disclosure id. */
  id: string;
  question: string;
  /**
   * Answer body. Accepts any React node so Storybook can pass JSX and the app
   * can pass RichText-rendered output. Always rendered into the DOM (kept there
   * even while collapsed) for SEO/GEO.
   */
  answer: React.ReactNode;
  /** When true, the item renders expanded on first paint (and without JS). */
  expandedByDefault?: boolean;
}

export interface FaqAccordionProps {
  /** Category title, rendered via the Heading component. */
  title: string;
  /** Ordered question/answer pairs. Rendered in array order. */
  items: FaqAccordionItem[];
  /** Number of items shown before the "show more" control appears. */
  initialVisibleCount?: number;
  /** Label for the control that reveals the remaining items. */
  showMoreLabel?: string;
  /** Label for the control that hides the extra items again. */
  showLessLabel?: string;
}

const DEFAULT_INITIAL_VISIBLE_COUNT = 5;

const FaqAccordion = ({
  title,
  items,
  initialVisibleCount = DEFAULT_INITIAL_VISIBLE_COUNT,
  showMoreLabel,
  showLessLabel,
}: FaqAccordionProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  // Contentful-provided labels win; the i18n catalog supplies the fallback so
  // no English literal ships in the library (sections.faq.* in libs/i18n).
  const moreLabel = showMoreLabel ?? intl.formatMessage({ id: 'sections.faq.showmore' });
  const lessLabel = showLessLabel ?? intl.formatMessage({ id: 'sections.faq.showless' });

  const hasOverflow = items.length > initialVisibleCount;
  // Show-more is the only JS-driven concern; everything else works without JS.
  const visibleItems = hasOverflow && !isExpanded ? items.slice(0, initialVisibleCount) : items;

  return (
    <section className="w-full">
      <Heading variant="h2">{title}</Heading>

      <ul className="mt-6 list-none border-t border-header-line p-0">
        {visibleItems.map(item => (
          <li key={item.id} className="border-b border-header-line">
            {/*
              `open` is set only as an initial attribute (spread when true, omitted otherwise)
              so React leaves <details> uncontrolled. A controlled `open={bool}` would reset a
              user-toggled item back on every re-render (e.g. when "show more" re-renders the list).
            */}
            <details className="group" {...(item.expandedByDefault ? { open: true } : {})}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-content marker:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary [&::-webkit-details-marker]:hidden">
                <span className="text-lg font-bold">{item.question}</span>
                <ChevronRight
                  aria-hidden="true"
                  className="h-5 w-5 shrink-0 rotate-90 text-primary transition-transform duration-200 group-open:-rotate-90 motion-reduce:transition-none"
                />
              </summary>
              {/* Answer uses the primary `text-content` token, not `text-content-muted`:
                  the muted gray (#979797 on white = 2.92:1) fails WCAG AA for body copy. */}
              <div className="pb-4 text-content">{item.answer}</div>
            </details>
          </li>
        ))}
      </ul>

      {hasOverflow && (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(previous => !previous)}
          className="mt-4 inline-flex items-center gap-2 font-bold text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {isExpanded ? lessLabel : moreLabel}
          <ChevronRight
            aria-hidden="true"
            className={`h-4 w-4 transition-transform duration-200 motion-reduce:transition-none ${
              isExpanded ? '-rotate-90' : 'rotate-90'
            }`}
          />
        </button>
      )}
    </section>
  );
};

export default withErrorBoundary(FaqAccordion, { componentName: 'FaqAccordion' });
